import { useEffect, useRef } from 'react';
import { ArrowRight, BookOpen, Shield, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const workLinks = [
  {
    icon: BookOpen,
    label: 'Lee el blog',
    path: '/blog',
    description: 'Guías de envío y consejos',
  },
  {
    icon: Shield,
    label: 'Recursos anti-fraude',
    path: '/anti-fraude',
    description: 'Protégete de estafas',
  },
  {
    icon: Heart,
    label: 'Filantropía y comunidad',
    path: '/filantropia',
    description: 'Nuestro impacto social',
  },
];

export function OurWork() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const content = contentRef.current;
    const image = imageRef.current;

    if (!section || !content || !image) return;

    const ctx = gsap.context(() => {
      const title = content.querySelector('h2');
      const body = content.querySelector('p');
      const links = content.querySelectorAll('.work-link');

      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: section,
          start: 'top 75%',
          once: true,
        },
      });

      tl.fromTo(
        image,
        { x: -40, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7 }
      )
        .fromTo(
          title,
          { x: 30, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6 },
          '-=0.5'
        )
        .fromTo(
          body,
          { x: 20, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.55 },
          '-=0.4'
        )
        .fromTo(
          links,
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, stagger: 0.08 },
          '-=0.3'
        );
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-white overflow-hidden py-24 sm:py-28 lg:py-36"
    >
      <div className="w-full flex items-center">
        <div className="mx-auto w-full max-w-[1120px] px-6 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Image */}
            <div ref={imageRef} className="relative hidden lg:block order-2 lg:order-1">
              <div className="relative aspect-video rounded-[32px] overflow-hidden">
                <img
                  src="/work_classroom.webp"
                  alt="Apoyo educativo y comunitario de Mi Envío Cuba en Cuba"
                  loading="lazy"
                  width="800"
                  height="450"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Content */}
            <div ref={contentRef} className="max-w-xl order-1 lg:order-2">
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
                Nuestro trabajo
              </h2>
              <p className="text-lg sm:text-xl text-text-secondary font-medium leading-snug mb-8">
                Publicamos guías de envío, protegemos a los clientes del fraude e
                invertimos en apoyo comunitario en toda Cuba.
              </p>

              {/* Work Links */}
              <div className="space-y-3">
                {workLinks.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className="work-link flex items-center justify-between p-4 rounded-2xl bg-bg-light hover:bg-line/60 transition-colors group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-brand" />
                      </div>
                      <div>
                        <span className="text-text-primary font-medium">
                          {item.label}
                        </span>
                        <p className="text-text-secondary text-sm">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-text-tertiary group-hover:text-brand group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
